import { useEffect } from "react";
import { toast } from "sonner";
import { useAuthStore } from "@/store/authStore";
import { endSession } from "@/lib/auth/endSession";
import {
  ACTIVITY_STORAGE_KEY,
  readLastActivity,
  recordActivity,
} from "@/lib/auth/activity";
import { authTimeMs, idleTimeoutMinutes, isMfaPending } from "@/lib/auth/jwt";

/** How long before the sign-out the reader is warned. */
export const IDLE_WARNING_MS = 60_000;

/** One warning at a time, dismissed by id when the reader comes back. */
export const IDLE_WARNING_TOAST_ID = "idle-sign-out-warning";

/** How often the deadline is checked. */
const CHECK_EVERY_MS = 1_000;

/**
 * Activity is shared with the other tabs at most this often. A pointer moving
 * raises dozens of events a second, and a clock counted in minutes does not
 * need any of them but the latest.
 */
const SHARE_EVERY_MS = 5_000;

/**
 * Input that means a person is at the console. Network traffic is deliberately
 * not here: polls, refetches and token refreshes run whether anyone is there
 * or not, so counting them would keep an abandoned session open forever.
 */
const INPUT_EVENTS = [
  "keydown",
  "mousedown",
  "mousemove",
  "pointerdown",
  "touchstart",
  "wheel",
] as const;

/**
 * Signs the session out after the idle timeout without keyboard, mouse, touch
 * or scroll input, in any tab of the console.
 *
 * The timeout comes from the token (`idle_timeout_minutes`), so the server's
 * setting is the one place it is decided; a token without it gets the standard
 * 30 minutes. A minute before the end the reader is warned and can stay.
 *
 * The last input time is shared through local storage, so working in one tab
 * keeps the others signed in, and a warning in one tab clears when the reader
 * comes back in another. A clock started before this session's own sign-in
 * (`auth_time`) never counts against it, so a new sign-in is not ended by the
 * previous session's idle time.
 *
 * Inactive for a pending MFA sign-in, whose short-lived pair ends by itself and
 * which has nothing behind it to protect yet.
 */
export function useIdleSignOut(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const active =
    isAuthenticated && accessToken != null && !isMfaPending(accessToken);
  const timeoutMs =
    active && accessToken ? idleTimeoutMinutes(accessToken) * 60_000 : 0;
  const sessionStart = active && accessToken ? (authTimeMs(accessToken) ?? 0) : 0;

  useEffect(() => {
    if (!active) return;

    const mountedAt = Date.now();
    let lastInputHere = 0;
    let lastShared = 0;
    let warned = false;

    const lastActive = () =>
      Math.max(lastInputHere, readLastActivity() ?? mountedAt, sessionStart);

    const dismissWarning = () => {
      if (!warned) return;
      warned = false;
      toast.dismiss(IDLE_WARNING_TOAST_ID);
    };

    const onInput = () => {
      const now = Date.now();
      lastInputHere = now;
      dismissWarning();
      if (now - lastShared >= SHARE_EVERY_MS) {
        lastShared = now;
        recordActivity(now);
      }
    };

    const check = () => {
      const remaining = lastActive() + timeoutMs - Date.now();
      if (remaining <= 0) {
        dismissWarning();
        endSession("idle");
        return;
      }
      if (remaining > IDLE_WARNING_MS) {
        // Another tab's input moved the deadline back out.
        dismissWarning();
        return;
      }
      if (!warned) {
        warned = true;
        toast.warning("You are about to be signed out", {
          id: IDLE_WARNING_TOAST_ID,
          description:
            "There has been no activity for a while, so you will be signed out in a minute.",
          duration: Infinity,
          action: { label: "Stay signed in", onClick: onInput },
        });
      }
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === ACTIVITY_STORAGE_KEY) check();
    };
    // A sleeping laptop runs no timers, and a background tab's are slowed to a
    // minute or more, so the deadline is checked the moment the tab is back.
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    for (const type of INPUT_EVENTS) {
      window.addEventListener(type, onInput, { passive: true });
    }
    // `scroll` does not bubble, so it is caught on the way down instead.
    document.addEventListener("scroll", onInput, {
      capture: true,
      passive: true,
    });
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    const interval = window.setInterval(check, CHECK_EVERY_MS);
    check();

    return () => {
      for (const type of INPUT_EVENTS) {
        window.removeEventListener(type, onInput);
      }
      document.removeEventListener("scroll", onInput, { capture: true });
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
      dismissWarning();
    };
  }, [active, timeoutMs, sessionStart]);
}
